/* Header behaviour. No framework; fails quiet into a plain flow header.
 *
 * The state machine:
 *   FLOW    at the top of the page: the masthead, in normal flow, scrolls
 *           away with the page like any other content.
 *   ARMED   once the visitor is 200px past the header's bottom edge it
 *           becomes position: fixed but stays tucked off-screen.
 *   SHOWN   any upward scroll slides the fixed bar in; downward tucks it.
 *   Back at the very top it hands over to the flow masthead again.
 *
 * The unit that moves is the chrome wrapper (top bar + header), so the
 * announcement bar always returns with the peek. A spacer keeps the flow
 * height while fixed, so the document never jumps underneath the reader.
 *
 * The added-to-cart popover pins the bar visible for as long as it shows,
 * because the popover is anchored to the cart icon inside it.
 */
(function () {
  'use strict';

  var header = document.querySelector('[data-ak-header]');
  if (!header) return;

  // The chrome unit: top bar plus header, moving as one, so the announcement
  // bar rides back in with the peeking header. A spacer holds the unit's
  // slot in the document while it is fixed.
  var chrome = header.closest('[data-ak-chrome]') || header.parentElement;
  var spacer = document.createElement('div');
  spacer.setAttribute('data-ak-chrome-spacer', '');
  spacer.setAttribute('aria-hidden', 'true');
  spacer.style.height = '0px';
  chrome.parentNode.insertBefore(spacer, chrome);

  var ARM_DISTANCE = 200; // px below the header's bottom edge before it arms
  var TOP_ZONE = 8;       // px from the top where flow always wins
  var JITTER = 5;         // ignore direction wobble smaller than this

  /* How far you have to travel UPWARD, in total, before the bar peeks in.
     A single scroll event's delta is only a few pixels, so gating on JITTER
     alone meant the faintest upward nudge summoned the whole masthead: on a
     phone, where a scroll rarely stops dead, that fired constantly. The
     travel is accumulated instead and reset the moment you head back down,
     so it takes a deliberate upward move rather than a wobble. */
  var REVEAL_TRAVEL = 15; // three times the old effective gate
  var upTravel = 0;

  var pinned = false;
  var lastY = window.scrollY;
  var flowHeight = 0;
  var bubbleSync = null; // assigned by the nav bubble block below

  function isFixed() {
    return chrome.classList.contains('is-fixed');
  }

  function isShowing() {
    return isFixed() && !chrome.classList.contains('is-tucked');
  }

  function armHidden() {
    if (isFixed()) return;
    flowHeight = chrome.offsetHeight;
    spacer.style.height = flowHeight + 'px';
    // Arm off-screen without letting the transform transition flash.
    chrome.classList.add('no-anim', 'is-fixed', 'is-tucked');
    void chrome.offsetHeight;
    chrome.classList.remove('no-anim');
  }

  var swapPending = false;
  var swapFinish = null; // the in-flight hand-back's listener, so it can be cancelled
  var swapTimer = 0;

  function show() {
    cancelSwap(); // a reveal outranks a queued exit, timer and listener included
    if (!isFixed()) armHidden();
    window.requestAnimationFrame(function () {
      chrome.classList.remove('is-tucked');
    });
  }

  function tuck() {
    if (isFixed()) chrome.classList.add('is-tucked');
  }

  /* Abandon an in-flight hand-back. The queued timer and the transitionend
     listener both outlive the flag, so clearing the flag alone lets a stale
     timer land inside the NEXT cycle and swap it mid-slide. */
  function cancelSwap() {
    swapPending = false;
    if (swapFinish) chrome.removeEventListener('transitionend', swapFinish);
    swapFinish = null;
    if (swapTimer) window.clearTimeout(swapTimer);
    swapTimer = 0;
  }

  /* The fixed bar never blinks out. If it is on screen when the flow swap is
     due, it slides up first and the swap happens once it is off-screen, where
     it cannot be seen. This is only ever correct MID-PAGE: use handBackNow()
     at the top, where the slide would hide the nav entirely. */
  function handBackToFlow() {
    if (!isFixed() || swapPending) return;

    if (chrome.classList.contains('is-tucked')) {
      flowSwap();
      return;
    }

    swapPending = true;
    chrome.classList.add('is-tucked');
    swapFinish = function () {
      cancelSwap();
      flowSwap();
    };
    chrome.addEventListener('transitionend', swapFinish);
    swapTimer = window.setTimeout(swapFinish, 380); // reduced motion or a missed event
  }

  /* At the very top there is nothing to slide away from: the flow header
     lands exactly where the fixed bar sits, so swapping instantly is
     invisible. Doing it immediately closes the window the animated hand-back
     opened, where the bar had already tucked off-screen but the flow header
     had not yet returned, leaving the top of the page with no nav at all. */
  function handBackNow() {
    cancelSwap();
    if (!isFixed()) return;
    flowSwap();
  }

  function flowSwap() {
    chrome.classList.add('no-anim');
    chrome.classList.remove('is-fixed', 'is-tucked');
    spacer.style.height = '0px';
    void chrome.offsetHeight;
    chrome.classList.remove('no-anim');
  }

  /* Publish the masthead's in-flow height so sections can size against the
     space actually left on screen. A full-height hero that assumes the
     viewport gets pushed down by the header and loses its last row below the
     fold, and the two stores do not carry the same chrome. Measured from the
     flow height, never the fixed bar, which is deliberately shorter. */
  function publishChromeHeight() {
    var h = isFixed() ? flowHeight : chrome.offsetHeight;
    if (h) document.documentElement.style.setProperty('--ak-chrome-h', h + 'px');
  }

  function armThreshold() {
    return (flowHeight || chrome.offsetHeight) + ARM_DISTANCE;
  }

  window.addEventListener('scroll', function () {
    var y = window.scrollY;
    var delta = y - lastY;
    lastY = y;

    if (bubbleSync) bubbleSync();

    if (pinned || navIsOpen()) {
      if (y > TOP_ZONE) show(); else handBackNow();
      return;
    }

    if (y <= TOP_ZONE) {
      handBackNow();
      return;
    }

    if (delta < -JITTER) {
      upTravel = upTravel - delta; // delta is negative going up

      if (y > armThreshold()) {
        // Upward, deep in the page: the bar peeks back in, but only once the
        // move is clearly intentional. Once it is showing, keep it showing:
        // re-gating every event would make it flicker as you continue up.
        if (upTravel >= REVEAL_TRAVEL || isShowing()) show();
      } else if (isFixed()) {
        // Within 200px of the nav's home the peek stands down and the unit
        // goes straight back into the flow, so the masthead arrives
        // naturally as you keep scrolling up.
        //
        // Tucking alone was not enough: it left the bar fixed and hidden
        // while its spacer still held the height, so anyone who stopped
        // short of the very top saw an empty band where the nav belongs.
        // The swap costs nothing visually because the spacer's height and
        // the flow header's height are the same, so nothing shifts.
        handBackToFlow();
      }
    } else if (delta > JITTER) {
      upTravel = 0; // heading down again: the upward run is over

      if (isFixed()) {
        tuck();
      } else if (y > armThreshold()) {
        armHidden();
      }
    }
  }, { passive: true });

  /* The rule is absolute: the top of the page always has the masthead on it.
     Several things move the page without a scroll sequence this handler can
     reason about (a bfcache restore, browser scroll restoration on a back
     navigation, an in-page anchor, the mobile address bar collapsing), so the
     invariant is re-asserted rather than inferred from the last event. */
  function guardTop() {
    if (window.scrollY <= TOP_ZONE) handBackNow();
    publishChromeHeight();
  }

  window.addEventListener('pageshow', guardTop);
  window.addEventListener('hashchange', guardTop);
  window.addEventListener('resize', guardTop, { passive: true });
  guardTop();
  /* Web fonts land after first paint and change the masthead's height, so the
     first measurement is re-taken once they have. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(publishChromeHeight);

  document.addEventListener('ak:cart:pop-open', function () {
    pinned = true;
    if (window.scrollY > TOP_ZONE) show();
  });

  document.addEventListener('ak:cart:pop-close', function () {
    pinned = false;
    lastY = window.scrollY; // resettle so the next real scroll decides
  });

  // ---- mobile nav disclosure ----------------------------------------------

  var toggle = header.querySelector('[data-ak-nav-toggle]');
  var panel = header.querySelector('[data-ak-nav-panel]');
  var iconOpen = header.querySelector('[data-ak-nav-icon-open]');
  var iconClose = header.querySelector('[data-ak-nav-icon-close]');

  function navIsOpen() {
    return toggle && toggle.getAttribute('aria-expanded') === 'true';
  }

  function setOpen(open) {
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      panel.setAttribute('data-open', '');
    } else {
      panel.removeAttribute('data-open');
    }
    if (iconOpen) iconOpen.hidden = open;
    if (iconClose) iconClose.hidden = !open;
  }

  if (toggle && panel) {
    toggle.addEventListener('click', function () {
      setOpen(!navIsOpen());
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && navIsOpen()) {
        setOpen(false);
        toggle.focus();
      }
    });

    document.addEventListener('click', function (e) {
      if (navIsOpen() && !panel.contains(e.target) && !toggle.contains(e.target)) {
        setOpen(false);
      }
    });
  }

  // ---- nav dropdowns ------------------------------------------------------
  // The CSS already opens these on hover and on focus-within, so a pointer
  // and a keyboard both work before this file has run. What is added here is
  // the click a pointer user expects, Escape, click-outside, and keeping
  // aria-expanded honest. Only one panel is ever open at a time.

  var dropdowns = [].slice.call(header.querySelectorAll('[data-ak-nav-dd]'));

  if (dropdowns.length) {
    var openDd = null;

    var closeDd = function () {
      if (!openDd) return;
      var btn = openDd.querySelector('[data-ak-nav-dd-btn]');
      openDd.removeAttribute('data-open');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      openDd = null;
    };

    dropdowns.forEach(function (dd) {
      var btn = dd.querySelector('[data-ak-nav-dd-btn]');
      if (!btn) return;

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasOpen = openDd === dd;
        closeDd();
        if (wasOpen) return;
        dd.setAttribute('data-open', '');
        btn.setAttribute('aria-expanded', 'true');
        openDd = dd;
      });
    });

    document.addEventListener('click', function (e) {
      if (openDd && !openDd.contains(e.target)) closeDd();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !openDd) return;
      var btn = openDd.querySelector('[data-ak-nav-dd-btn]');
      closeDd();
      if (btn) btn.focus();
    });

    /* the bar tucks and untucks as you scroll, and a panel left open while
       its own trigger slides away is a menu floating over the page */
    window.addEventListener('scroll', function () { closeDd(); }, { passive: true });
  }

  // ---- launchpad nudge ----------------------------------------------------
  // Shows once after the page settles, and never again after the visitor
  // dismisses it or follows it. Anchored in the nav, so it travels with the
  // header through every state without any positioning code.

  var bubble = header.querySelector('[data-ak-nav-bubble]');
  if (bubble) {
    var BUBBLE_KEY = 'ak_launchpad_bubble_v1';
    var BUBBLE_ZONE = 40; // px: the bubble lives only at the top of the page
    var bubbleDismissed = false;
    var bubbleReady = false;
    try { bubbleDismissed = window.localStorage.getItem(BUBBLE_KEY) === '1'; } catch (e) { /* private mode */ }

    /* Scrolling away hides it without dismissing; returning to the top
       brings it back (the entrance animation replays on re-display).
       Only an explicit dismiss or follow retires it for good. */
    bubbleSync = function () {
      bubble.hidden = !(bubbleReady && !bubbleDismissed && window.scrollY <= BUBBLE_ZONE);
    };

    var retireBubble = function () {
      bubbleDismissed = true;
      try { window.localStorage.setItem(BUBBLE_KEY, '1'); } catch (e) { /* still hide for this page */ }
      bubble.hidden = true;
    };

    if (!bubbleDismissed) {
      window.setTimeout(function () {
        bubbleReady = true;
        bubbleSync();
      }, 1400);
    }

    bubble.addEventListener('click', function (e) {
      if (e.target.closest('[data-ak-nav-bubble-dismiss]')) e.preventDefault();
      // Following the link retires it too: the message has done its job.
      retireBubble();
    });
  }

  // ---- cart pill ----------------------------------------------------------
  // Primary source: the drawer announces every change. Fallback: wrap fetch
  // so cart mutations from app embeds still update the pill.

  var countEl = header.querySelector('[data-ak-cart-count]');

  function setCount(n) {
    if (!countEl || typeof n !== 'number') return;
    countEl.textContent = String(n);
    countEl.setAttribute('data-count', String(n));
    countEl.classList.add('is-bumped');
    window.setTimeout(function () { countEl.classList.remove('is-bumped'); }, 220);
  }

  document.addEventListener('ak:cart:changed', function (e) {
    setCount(e.detail.itemCount);
  });

  if (countEl && window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function () {
      var call = origFetch.apply(this, arguments);
      try {
        var url = String(arguments[0] && arguments[0].url ? arguments[0].url : arguments[0]);
        if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url) && !/sections=/.test(url)) {
          call.then(function (res) {
            if (!res.ok) return;
            origFetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (cart) { if (cart) setCount(cart.item_count); })
              .catch(function () {});
          /* the caller owns the original rejection; this derived chain must
             swallow its own or a blocked or dropped request logs an unhandled
             rejection against this file */
          }).catch(function () {});
        }
      } catch (e) { /* counting is cosmetic, never break the request */ }
      return call;
    };
  }

  /* ---- store switcher -------------------------------------------------
     The markup ships collapsed, so nothing flashes open before this deferred
     script runs. That matters most on the mobile bar, where the popover is
     absolutely positioned over the page.

     The inline variant is the exception: it is meant to stay laid out, so it
     is expanded here rather than in the markup, for the same reason. */
  (function () {
    var wraps = [].slice.call(document.querySelectorAll('[data-ak-cty]'));
    if (!wraps.length) return;

    var open = null;

    function close() {
      if (!open) return;
      open.pop.hidden = true;
      open.btn.setAttribute('aria-expanded', 'false');
      open = null;
    }

    wraps.forEach(function (wrap) {
      var btn = wrap.querySelector('[data-ak-cty-btn]');
      var pop = wrap.querySelector('[data-ak-cty-pop]');
      if (!btn || !pop) return;

      /* inline (the mobile panel) stays laid out; only the header collapses */
      if (wrap.classList.contains('ak-cty--inline')) {
        pop.hidden = false;
        return;
      }

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasOpen = open && open.pop === pop;
        close();
        if (wasOpen) return;
        pop.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        open = { btn: btn, pop: pop };
      });
    });

    document.addEventListener('click', function (e) {
      if (open && !open.pop.contains(e.target) && !open.btn.contains(e.target)) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !open) return;
      var btn = open.btn;
      close();
      btn.focus();
    });

    /* the header tucks and untucks as you scroll; a popover left open while
       its own button slides away is a menu floating over the page */
    window.addEventListener('scroll', function () { close(); }, { passive: true });
  })();
})();
